import React, { useEffect, useMemo, useState, useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const BASE_URL = "https://nissi-india.com";

const ContractorPaymentEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HEAD";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branchTypeLS,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userId]
  );

  // ✅ edit state from listing
  const editMode = Boolean(location?.state?.editMode);
  const contractorPayId = location?.state?.contractorPayId || null;

  // Form state
  const [formData, setFormData] = useState({
    date: "",
    billNo: "",
    contractorId: "",
    projectId: "",
    workTypeId: "",
    opAmount: "",
    paidToday: "",
    balanceAmount: "",
  });

  // dropdown states
  const [ddlContractors, setDdlContractors] = useState([]);
  const [ddlProjects, setDdlProjects] = useState([]);
  const [ddlWorkTypes, setDdlWorkTypes] = useState([]);

  const [loading, setLoading] = useState(false);

  const canLoadWorkTypes = useMemo(
    () => formData.contractorId && formData.projectId,
    [formData.contractorId, formData.projectId]
  );

  const canLoadOpAmount = useMemo(
    () => formData.contractorId && formData.projectId && formData.workTypeId,
    [formData.contractorId, formData.projectId, formData.workTypeId]
  );

  // ---------- helpers ----------
  const calcBalance = (op, paid) => {
    const opAmount = parseFloat(op) || 0;
    const paidToday = parseFloat(paid) || 0;
    return (opAmount - paidToday).toFixed(2);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "opAmount" || field === "paidToday") {
        updated.balanceAmount = calcBalance(updated.opAmount, updated.paidToday);
      }

      return updated;
    });
  };

  const handleBack = () => navigate(-1);

  // ---------- API calls ----------
  const loadContractors = async () => {
    const res = await axios.get(`${BASE_URL}/api/contractorEntry/contractors`, {
      params: { branchid: branchIdLS },
      headers: commonHeaders,
    });
    if (res.data.success) setDdlContractors(res.data.data || []);
  };

  const loadProjectsInitial = async () => {
    const res = await axios.get(`${BASE_URL}/api/contractorEntry/projects`, {
      params: { branchid: branchIdLS, branchtype: branchTypeLS },
      headers: commonHeaders,
    });
    if (res.data.success) setDdlProjects(res.data.data || []);
  };

  const loadProjectsByContractor = async (contractorId) => {
    const res = await axios.get(
      `${BASE_URL}/api/contractorEntry/projectsByContractor`,
      {
        params: { contractorid: contractorId },
        headers: commonHeaders,
      }
    );
    if (res.data.success) setDdlProjects(res.data.data || []);
  };

  const loadWorkTypes = async (contractorId, projectId) => {
    const res = await axios.get(`${BASE_URL}/api/contractorEntry/worktypes`, {
      params: { contractorid: contractorId, projectid: projectId },
      headers: commonHeaders,
    });
    if (res.data.success) setDdlWorkTypes(res.data.data || []);
  };

  const loadOpAmount = async (contractorId, projectId, workTypeId) => {
    const res = await axios.get(`${BASE_URL}/api/contractorEntry/opamount`, {
      params: {
        contractorid: contractorId,
        projectid: projectId,
        worktypeid: workTypeId,
      },
      headers: commonHeaders,
    });
    if (res.data.success) {
      const op = res.data.opAmount ?? "";
      setFormData((prev) => ({
        ...prev,
        opAmount: op,
        balanceAmount: calcBalance(op, prev.paidToday),
      }));
    }
  };

  const loadNextBillNo = async () => {
    const res = await axios.get(`${BASE_URL}/api/contractorEntry/nextBillNo`, {
      params: { branchid: branchIdLS },
      headers: commonHeaders,
    });
    if (res.data.success) {
      setFormData((prev) => ({ ...prev, billNo: res.data.billNo || "" }));
    }
  };

  const loadEditRecord = async () => {
    if (!contractorPayId) return;

    const res = await axios.get(`${BASE_URL}/api/contractorEntry/getById`, {
      params: { contractorpayid: contractorPayId },
      headers: commonHeaders,
    });

    if (!res.data.success) {
      toast.error(res.data.message || "Edit record not found");
      return;
    }

    const r = res.data.data;

    const toISODate = (ddmmyyyy) => {
      if (!ddmmyyyy) return "";
      const [dd, mm, yyyy] = String(ddmmyyyy).split("/");
      if (!dd || !mm || !yyyy) return "";
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    };

    setFormData((prev) => ({
      ...prev,
      billNo: r.fld_billno || "",
      date: toISODate(r.fld_date),
      contractorId: String(r.fld_contractorid || ""),
      projectId: String(r.fld_projectid || ""),
      workTypeId: String(r.fld_worktypeid || ""),
      opAmount: r.fld_opamount ?? "",
      paidToday: r.fld_paidamount ?? "",
      balanceAmount: r.fld_balamount ?? calcBalance(r.fld_opamount, r.fld_paidamount),
    }));

    await loadProjectsByContractor(String(r.fld_contractorid || ""));
    await loadWorkTypes(
      String(r.fld_contractorid || ""),
      String(r.fld_projectid || "")
    );
  };

  // ---------- events for cascading ddl ----------
  const onContractorChange = async (contractorId) => {
    handleInputChange("contractorId", contractorId);

    setFormData((prev) => ({
      ...prev,
      contractorId,
      projectId: "",
      workTypeId: "",
      opAmount: "",
      paidToday: "",
      balanceAmount: "",
    }));
    setDdlWorkTypes([]);

    if (!contractorId) {
      await loadProjectsInitial();
      return;
    }

    try {
      await loadProjectsByContractor(contractorId);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load Projects");
    }
  };

  const onProjectChange = async (projectId) => {
    handleInputChange("projectId", projectId);

    setFormData((prev) => ({
      ...prev,
      projectId,
      workTypeId: "",
      opAmount: "",
      paidToday: "",
      balanceAmount: "",
    }));
    setDdlWorkTypes([]);

    if (formData.contractorId && projectId) {
      try {
        await loadWorkTypes(formData.contractorId, projectId);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load Work Types");
      }
    }
  };

  const onWorkTypeChange = async (workTypeId) => {
    handleInputChange("workTypeId", workTypeId);

    setFormData((prev) => ({
      ...prev,
      workTypeId,
      opAmount: "",
      paidToday: "",
      balanceAmount: "",
    }));

    if (formData.contractorId && formData.projectId && workTypeId) {
      try {
        await loadOpAmount(formData.contractorId, formData.projectId, workTypeId);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load Op Amount");
      }
    }
  };

  // ---------- submit ----------
  const handleSubmit = async () => {
    try {
      if (!branchIdLS) return toast.error("BranchId missing!");
      if (!userId) return toast.error("UserId missing!");

      const required = [
        "date",
        "billNo",
        "contractorId",
        "projectId",
        "workTypeId",
        "paidToday",
      ];
      const missing = required.filter((k) => !String(formData[k] ?? "").trim());
      if (missing.length) return toast.error("Please fill all required fields!");

      const paid = parseFloat(formData.paidToday) || 0;
      const op = parseFloat(formData.opAmount) || 0;
      if (paid <= 0) return toast.error("Paid Today must be greater than 0");
      if (paid > op) return toast.error("Paid Today cannot exceed Op Amount");

      setLoading(true);

      const payload = {
        editMode,
        contractorpayid: contractorPayId,
        branchid: Number(branchIdLS),
        loginuserid: Number(userId),
        date: formData.date,
        billNo: formData.billNo,
        contractorid: Number(formData.contractorId),
        projectid: Number(formData.projectId),
        worktypeid: Number(formData.workTypeId),
        opamount: Number(formData.opAmount || 0),
        paidamount: Number(formData.paidToday || 0),
        balamount: Number(calcBalance(formData.opAmount, formData.paidToday)),
      };

      const res = await axios.post(
        `${BASE_URL}/api/contractorEntry/save`,
        payload,
        {
          headers: commonHeaders,
        }
      );

      if (res.data.success) {
        toast.success(
          editMode
            ? "Updated Successfully!"
            : `Saved Successfully! (${res.data.billNo || ""})`
        );
        setTimeout(() => navigate("/PaymentVouchers/ContractorPaymentListing"), 800);
      } else {
        toast.error(res.data.message || "Save failed");
      }
    } catch (err) {
      console.error("Save Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  // ---------- initial load ----------
  useEffect(() => {
    (async () => {
      try {
        if (!branchIdLS) {
          toast.error("BranchId not found in localStorage!");
          return;
        }

        await loadContractors();

        if (editMode && contractorPayId) {
          await loadEditRecord();
        } else {
          await loadProjectsInitial();
          await loadNextBillNo();
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load Entry page data");
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONTRACTOR PAYMENT ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Contractor Payment Entry {editMode ? "(Edit)" : ""}
            </h1>

            {editMode && contractorPayId && (
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Pay ID: <b>{contractorPayId}</b>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Contractor Name <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.contractorId}
                  onChange={(e) => onContractorChange(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {ddlContractors.map((x) => (
                    <option key={`${x.value}-${x.label}`} value={x.value}>
                      {x.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Work Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.workTypeId}
                  onChange={(e) => onWorkTypeChange(e.target.value)}
                  disabled={!canLoadWorkTypes}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    !canLoadWorkTypes
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {ddlWorkTypes.map((x) => (
                    <option key={`${x.value}-${x.label}`} value={x.value}>
                      {x.label}
                    </option>
                  ))}
                </select>
                {!canLoadWorkTypes && (
                  <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Select Contractor + Project to load work types
                  </div>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Op Amount
                </label>
                <input
                  type="number"
                  value={formData.opAmount}
                  readOnly
                  className={`w-full px-4 py-2 border rounded-lg font-semibold ${
                    isDark ? "bg-gray-700 border-gray-600 text-green-400" : "bg-white border-gray-300 text-green-600"
                  }`}
                  placeholder="Auto from backend"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Balance Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.balanceAmount}
                  readOnly
                  className={`w-full px-4 py-2 border rounded-lg font-semibold ${
                    isDark ? "bg-gray-700 border-gray-600 text-orange-400" : "bg-white border-gray-300 text-orange-600"
                  }`}
                  placeholder="Calculated"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Bill No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.billNo}
                  readOnly={!editMode}
                  onChange={(e) => handleInputChange("billNo", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="Auto generated"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Project Name <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.projectId}
                  onChange={(e) => onProjectChange(e.target.value)}
                  disabled={!formData.contractorId}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    !formData.contractorId
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {ddlProjects.map((x) => (
                    <option key={`${x.value}-${x.label}`} value={x.value}>
                      {x.label}
                    </option>
                  ))}
                </select>
                {!formData.contractorId && (
                  <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Select Contractor to load project list
                  </div>
                )}
              </div>

              <div />

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Paid Today <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.paidToday}
                  onChange={(e) => handleInputChange("paidToday", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg font-semibold ${
                    isDark ? "bg-gray-700 border-gray-600 text-blue-400" : "bg-white border-gray-300 text-blue-600"
                  }`}
                  min="0"
                  step="0.01"
                  placeholder="Enter payment amount"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center items-center gap-6">
            <button
              onClick={handleBack}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark ? "bg-gray-600 hover:bg-gray-700 text-white" : "bg-gray-500 hover:bg-gray-600 text-white"
              }`}
            >
              <FaArrowLeft />
              Back
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                loading ? "bg-gray-400 cursor-not-allowed text-white" : `${buttonClass} ${hoverClass} text-white`
              }`}
            >
              <FaSave />
              {loading ? "Saving..." : editMode ? "Update" : "Submit"}
            </button>
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorPaymentEntry;